'use client';

import { BRAND, withBase } from '../lib/brand';
import React from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import {
  EnvironmentOutlined,
  CompassOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { AppStoreBadges } from './AppStoreBadges';
import dynamic from 'next/dynamic';
import { useCaPreview } from '../lib/caPreview';

// AAA store footer (ca_preview only), lazy so it costs nothing while off.
const AaaFooter = dynamic(() => import('./store/AaaFooter'), { ssr: false });

const APP_STORE_URL = 'https://apps.apple.com/au/app/camping-australia/id6761671553';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.campingaustralia.android';

const popularDestinations = [
  { label: 'New South Wales', href: withBase('/regions/states/new-south-wales') },
  { label: 'Victoria', href: withBase('/regions/states/victoria') },
  { label: 'Queensland', href: withBase('/regions/states/queensland') },
  { label: 'Western Australia', href: withBase('/regions/states/western-australia') },
  { label: 'South Australia', href: withBase('/regions/states/south-australia') },
  { label: 'Tasmania', href: withBase('/regions/states/tasmania') },
  { label: 'Northern Territory', href: withBase('/regions/states/northern-territory') },
  { label: 'Australian Capital Territory', href: withBase('/regions/states/australian-capital-territory') },
];

const popularRoutes = [
  { label: 'Sydney to Byron Bay', start: 'Sydney', end: 'Byron Bay' },
  { label: 'Melbourne to Adelaide', start: 'Melbourne', end: 'Adelaide' },
  { label: 'Cairns to Cape Tribulation', start: 'Cairns', end: 'Cape Tribulation' },
  { label: 'Perth to Esperance', start: 'Perth', end: 'Esperance' },
  { label: 'Adelaide to Alice Springs', start: 'Adelaide', end: 'Alice Springs' },
  { label: 'Hobart to Cradle Mountain', start: 'Hobart', end: 'Cradle Mountain' },
];

const Footer: React.FC = () => {
  const t = useTranslations('footer');
  const preview = useCaPreview();
  if (preview) return <AaaFooter />;
  // i18n phase 1: only the shared + escapeto-visible footer strings are
  // translated. The "Browse by Region" / "Popular Routes" columns, the
  // Explore sub-links (All Regions, Campervan Hire, Starlink, Caravan Finance,
  // About Us, Shop), the app-store links and the About/Contact/Privacy/Terms
  // bar are all camping-australia-only branches (never rendered on escapeto),
  // so they stay English literals. i18n TODO: translate if these surfaces are
  // ever enabled for escapeto.
  return (
    <footer className="bg-header-bg text-white mt-auto" data-site-footer="">
      {/* Main footer content */}
      <div className="container mx-auto px-4 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <h3 className="font-heading text-lg font-bold mb-3">{BRAND.name}</h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-4">
              {BRAND.key === 'camping-australia'
                ? 'Discover amazing camping destinations across Australia. Find free camping, caravan parks, and beautiful campsites for your next adventure.'
                : t('tagline', { place: BRAND.countryName })}
            </p>
            <div className="flex flex-col gap-2 mb-5">
              <Link
                href={withBase("/owner-portal")}
                className="group flex items-center gap-3 rounded-[10px] border border-white/10 bg-white/[0.03] px-3.5 py-3 transition hover:border-white/20 hover:bg-white/[0.06] active:scale-[0.99]"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/[0.18] text-accent">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <rect x="2" y="7" width="20" height="14" rx="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block text-[13px] font-semibold text-white">{t('manageCampsite')}</span>
                  <span className="mt-0.5 block text-[11px] text-slate-400">{t('manageCampsiteSub')}</span>
                </span>
                <svg className="h-4 w-4 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="m9 18 6-6-6-6"/>
                </svg>
              </Link>
              <Link
                href={withBase("/recommend-campsite")}
                className="group flex items-center gap-3 rounded-[10px] border border-white/10 bg-white/[0.03] px-3.5 py-3 transition hover:border-white/20 hover:bg-white/[0.06] active:scale-[0.99]"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-slate-300">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block text-[13px] font-semibold text-white">{t('suggestCampsite')}</span>
                  <span className="mt-0.5 block text-[11px] text-slate-400">{t('suggestCampsiteSub')}</span>
                </span>
                <svg className="h-4 w-4 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="m9 18 6-6-6-6"/>
                </svg>
              </Link>
            </div>
            {/* App store badges — Camping Australia native apps */}
            {BRAND.showAppBanner && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Get the app
                </p>
                <AppStoreBadges size="sm" />
              </div>
            )}
          </div>

          {/* Browse by Region — AU-only (no Region nodes elsewhere) */}
          {BRAND.showRegions && (
          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
              <EnvironmentOutlined style={{ fontSize: '14px' }} />
              Browse by Region
            </h4>
            <ul className="space-y-2">
              {popularDestinations.map((item) => (
                <li key={item.label}>
                  <Link
                    href={item.href}
                    className="text-sm text-slate-400 transition hover:text-white"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          )}

          {/* Popular Routes — AU route seeds */}
          {BRAND.key === 'camping-australia' && (
          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
              <CarOutlined style={{ fontSize: '14px' }} />
              Popular Routes
            </h4>
            <ul className="space-y-2">
              {popularRoutes.map((item) => (
                <li key={item.label}>
                  <Link
                    href={withBase(`?isRouteSearch=true&startPoint=${encodeURIComponent(item.start)}&endPoint=${encodeURIComponent(item.end)}`)}
                    className="text-sm text-slate-400 transition hover:text-white"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          )}

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
              <CompassOutlined style={{ fontSize: '14px' }} />
              {t('explore')}
            </h4>
            <ul className="space-y-2">
              {BRAND.showRegions && (
                <li>
                  <Link href={withBase("/regions")} className="text-sm text-slate-400 transition hover:text-white">
                    All Regions
                  </Link>
                </li>
              )}
              <li>
                <Link href={withBase("/saved")} className="text-sm text-slate-400 transition hover:text-white">
                  {t('savedCampsites')}
                </Link>
              </li>
              {BRAND.showVanHire && (
                <li>
                  <Link href={withBase("/campervan-hire")} className="text-sm text-slate-400 transition hover:text-white">
                    Campervan Hire
                  </Link>
                </li>
              )}
              {BRAND.key === 'camping-australia' && (
                <>
                  <li>
                    <Link href={withBase("/starlink-for-camping")} className="text-sm text-slate-400 transition hover:text-white">
                      Starlink & Off-Grid Internet
                    </Link>
                  </li>
                  <li>
                    <Link href={withBase("/caravan-finance")} className="text-sm text-slate-400 transition hover:text-white">
                      Caravan Finance
                    </Link>
                  </li>
                  <li>
                    <Link href={withBase("/caravans")} className="text-sm text-slate-400 transition hover:text-white">
                      Caravan Buying Guide
                    </Link>
                  </li>
                  <li>
                    <a href="https://campingaustralia.com.au/pages/about-outdoor-camping-australia" className="text-sm text-slate-400 transition hover:text-white">
                      About Us
                    </a>
                  </li>
                  <li>
                    <a href="https://campingaustralia.com.au/collections/all" className="text-sm text-slate-400 transition hover:text-white">
                      Shop
                    </a>
                  </li>
                </>
              )}
              {BRAND.showAppBanner && (
                <>
                  <li>
                    <a
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-slate-400 transition hover:text-white"
                    >
                      iOS App
                    </a>
                  </li>
                  <li>
                    <a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-slate-400 transition hover:text-white"
                    >
                      Android App
                    </a>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-800">
        {/* pb-24: the floating "AI Camping Assistant" bubble (TripChat, fixed
            bottom-6 ≈ 72px tall incl. offset, both brands) sat on top of the
            Privacy / Terms links at the bottom right. Reserving 96px below
            the bar lets those links scroll clear of it at every width. */}
        <div className="site-footer-bottom container mx-auto px-4 pt-4 pb-24 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} {BRAND.name}. {t('allRightsReserved')}.
          </p>
          {BRAND.key === 'camping-australia' && (
          <nav className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <a href="https://campingaustralia.com.au/pages/about-outdoor-camping-australia" className="text-slate-500 transition hover:text-slate-300">
              About
            </a>
            <a href="https://campingaustralia.com.au/pages/contact-us" className="text-slate-500 transition hover:text-slate-300">
              Contact
            </a>
            <a href="https://campingaustralia.com.au/policies/privacy-policy" className="text-slate-500 transition hover:text-slate-300">
              Privacy
            </a>
            <a href="https://campingaustralia.com.au/policies/terms-of-service" className="text-slate-500 transition hover:text-slate-300">
              Terms
            </a>
          </nav>
          )}
          {/* EscapeTo's own policy pages (app/privacy, app/terms, app/support).
              Plain un-prefixed paths: they live outside the country trees, so
              withBase() (which adds /us etc.) is deliberately not used. */}
          {BRAND.key === 'escapeto' && (
          <nav className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <a href="/support" className="text-slate-500 transition hover:text-slate-300">
              {t('help')}
            </a>
            <a href="/privacy" className="text-slate-500 transition hover:text-slate-300">
              {t('privacy')}
            </a>
            <a href="/terms" className="text-slate-500 transition hover:text-slate-300">
              {t('terms')}
            </a>
          </nav>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
